import { memo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Badge } from "@/shared/components/ui/badge";
import {
  formatCEP,
  formatDate,
  formatDocument,
  formatPhone,
} from "@/shared/lib/formatters";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { ClientRentalsSection } from "@/features/rentals/components/ClientRentalsSection";
import { PersonType } from "@/shared/types/enums";
import type { ClientResponseDTO } from "../types/client.types";

interface ClientDetailsModalProps {
  open: boolean;
  client: ClientResponseDTO | null;
  onClose: () => void;
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field = memo(function Field({ label, children }: FieldProps) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
});

export const ClientDetailsModal = memo(function ClientDetailsModal({
  open,
  client,
  onClose,
}: ClientDetailsModalProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{client ? client.name : "Cliente"}</DialogTitle>
          <DialogDescription>
            {client ? formatDocument(client.document) : ""}
          </DialogDescription>
        </DialogHeader>

        {client ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="Tipo">
                <Badge variant="secondary">
                  {client.personType === PersonType.JURIDICA
                    ? "Pessoa jurídica"
                    : "Pessoa física"}
                </Badge>
              </Field>
              <Field label="Documento">{formatDocument(client.document)}</Field>
              <Field label="Gênero">
                {client.gender
                  ? client.gender === "M"
                    ? "Masculino"
                    : "Feminino"
                  : "—"}
              </Field>
              <Field label="Telefone">
                {client.phone.length >= 10
                  ? formatPhone(client.phone)
                  : client.phone}
              </Field>
              <Field label="Email">{client.email || "—"}</Field>
              <Field label="Cliente desde">
                {formatDate(client.createdAt)}
              </Field>
              <Field label="Endereço">
                {client.street}, {client.number}
                {client.complement ? ` — ${client.complement}` : ""}
              </Field>
              <Field label="Bairro">{client.neighborhood}</Field>
              <Field label="Estado">{client.state}</Field>
              <Field label="CEP">
                {client.postalCode.length === 8
                  ? formatCEP(client.postalCode)
                  : client.postalCode}
              </Field>
            </div>

            <Can permission={ACTIONS.rentals.read}>
              <ClientRentalsSection clientId={client.id} />
            </Can>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
});
